import { useMemo } from "react";
import type { FieldDefinition } from "../fields/types.js";
import { evaluateLogic } from "../logic/evaluate.js";
import type { LogicRule } from "../logic/types.js";
import { FieldRenderer } from "./FieldRenderer.jsx";
import { confirmKeyFor, confirmLabelFor, wantsConfirmation } from "../fields/confirmable.js";
import type { RendererBranding, RendererLookups, RendererSection } from "./types.js";
import { groupIntoRows, spanClassFor, spansFor } from "./layout.js";

/**
 * Renders one page of a form: sections → row groups → fields, with conditional logic applied.
 *
 * Shared by the builder's live preview and (from Phase 5) `apps/public`, so what a staff member
 * previews is literally what an applicant will see — not a lookalike that drifts.
 */
export interface FormRendererProps {
  fields: readonly FieldDefinition[];
  sections: readonly RendererSection[];
  logic: readonly LogicRule[];
  values: Record<string, unknown>;
  errors?: Record<string, string>;
  onChange: (key: string, value: unknown) => void;
  branding: RendererBranding;
  lookups?: RendererLookups;
  /** Restrict rendering to one page. Omit to render every page at once. */
  pageNumber?: number;
  disabled?: boolean;
  /**
   * Field keys locked regardless of the whole-form `disabled` flag — e.g. the field bound to
   * `person.email` once identity is OTP-verified: it can never diverge from the address the
   * applicant actually proved they own (apps/public/src/FormFlow.tsx). A UX nicety only — the
   * server enforces the same rule authoritatively at submit (services/submission.ts), since a
   * client-side lock alone would just be trusting the client for something it already knows.
   */
  readOnlyKeys?: ReadonlySet<string>;
  /** Builder-only: show fields that logic has hidden, dimmed, instead of removing them. */
  revealHidden?: boolean;
}

export const FormRenderer = ({
  fields,
  sections,
  logic,
  values,
  errors,
  onChange,
  branding,
  lookups,
  pageNumber,
  disabled,
  readOnlyKeys,
  revealHidden,
}: FormRendererProps): JSX.Element => {
  const logicResult = useMemo(
    () => evaluateLogic(logic, values, fields as FieldDefinition[]),
    [logic, values, fields],
  );

  const hiddenFields = new Set(logicResult.hiddenFieldKeys);
  const hiddenSections = new Set(logicResult.hiddenSectionKeys);

  const visibleSections = sections
    .filter((section) => pageNumber === undefined || section.pageNumber === pageNumber)
    .filter((section) => revealHidden || !hiddenSections.has(section.key))
    .sort((a, b) => a.position - b.position);

  /** Fields sharing a rowGroup sit side by side (docs/01-PRODUCT-SPEC.md §3.1). */
  const rowsFor = (sectionKey: string | null): FieldDefinition[][] =>
    groupIntoRows(
      fields
        .filter((field) => (field.sectionKey ?? null) === sectionKey)
        .filter((field) => field.visibility !== "admin_only")
        .filter((field) => revealHidden || !hiddenFields.has(field.key)),
    );

  const renderRows = (sectionKey: string | null): JSX.Element[] =>
    rowsFor(sectionKey).map((row, index) => {
      const spans = spansFor(row);
      return (
        <div
          key={`${sectionKey ?? "root"}-row-${index}`}
          className={row.length > 1 ? "grid grid-cols-12 gap-4" : ""}
        >
          {row.map((field, cell) => (
            <div
              key={field.key}
              className={[
                row.length > 1 ? `col-span-12 ${spanClassFor(spans[cell] ?? 12)}` : "",
                revealHidden && hiddenFields.has(field.key) ? "opacity-40" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <FieldRenderer
                field={field}
                value={values[field.key]}
                onChange={(next) => onChange(field.key, next)}
                error={errors?.[field.key]}
                disabled={disabled || readOnlyKeys?.has(field.key)}
                branding={branding}
                lookups={lookups}
              />

              {/* The generated "type it again" input. Not a second field in the schema — it is the
                  same field rendered twice, so it cannot drift from the original's type or rules,
                  and its answer is stripped before anything is stored. */}
              {wantsConfirmation(field.config as Record<string, unknown>) && (
                <div className="mt-3">
                  <FieldRenderer
                    field={{
                      ...field,
                      key: confirmKeyFor(field.key),
                      label: confirmLabelFor(field.label, field.config as Record<string, unknown>),
                      // The help text belongs to the question, not to the repeat of it.
                      config: { ...field.config, helpText: undefined },
                    }}
                    value={values[confirmKeyFor(field.key)]}
                    onChange={(next) => onChange(confirmKeyFor(field.key), next)}
                    error={errors?.[confirmKeyFor(field.key)]}
                    disabled={disabled || readOnlyKeys?.has(field.key)}
                    branding={branding}
                    lookups={lookups}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      );
    });

  return (
    // `@container` (Tailwind 3.4+, no plugin) rather than a viewport media query: the row layout
    // below has to respond to *this* box's actual rendered width. On a real applicant page that box
    // is the viewport in practice, so behaviour there is unchanged; inside the builder's preview
    // pane it is a simulated device width the coordinator picked, which a viewport query cannot see
    // at all — every size looked identical because none of them ever reached the 640px viewport
    // breakpoint the old `sm:` class depended on.
    <div
      className="@container space-y-6"
      style={{
        background: branding.background,
        color: branding.text,
        fontFamily: branding.bodyFont,
      }}
    >
      {/* Fields not assigned to any section still render, above the sections. */}
      {rowsFor(null).length > 0 && <div className="space-y-4">{renderRows(null)}</div>}

      {visibleSections.map((section) => (
        <section
          key={section.key}
          className="rounded-lg border p-4"
          style={{ borderColor: branding.border, background: branding.surface }}
          aria-labelledby={section.title ? `section-${section.key}` : undefined}
        >
          {section.title && (
            <h2
              id={`section-${section.key}`}
              className="mb-1 text-lg font-semibold"
              style={{ color: branding.primary, fontFamily: branding.headingFont }}
            >
              {section.title}
            </h2>
          )}
          {section.description && <p className="mb-4 text-sm opacity-75">{section.description}</p>}
          <div className="space-y-4">{renderRows(section.key)}</div>
        </section>
      ))}
    </div>
  );
};
